import type { Options } from 'ky';
import { fetchJSON, HttpError } from '../api';
import type {
  CommitAndClaimNextReasonTaskResult,
  CommitReasonTaskInput,
  PatchReasonSegmentInput,
  ReasonAdminTask,
  ReasonAdminTaskListResponse,
  ReasonClientEventInput,
  ReasonIngestionResult,
  ReasonLeaderboardData,
  ReasonLeaderboardResponse,
  ReasonOption,
  ReasonResultDetail,
  ReasonResultListResponse,
  ReasonRuntimeInfo,
  ReasonSegmentContractV2Options,
  ReasonSegmentContractV2Result,
  ReasonSourceDiagnostic,
  ReasonSourceRefreshResponse,
  ReasonStats,
  ReasonStatsResponse,
  ReasonSubmissionHistoryEntry,
  ReasonTask,
  ReasonTaskListResponse,
  ReasonTaskResponse,
  ReasonTaskSummary,
  SecCapReasonApiClient,
} from './types';

const API_ROOT = '/api/seccapreason';

export class SecCapReasonApiError extends Error {
  readonly status: number;
  readonly code: string | null;

  constructor(message: string, status: number, code: string | null = null) {
    super(message);
    this.name = 'SecCapReasonApiError';
    this.status = status;
    this.code = code;
  }
}

type ErrorPayload = {
  error?: string;
  message?: string;
  code?: string;
  detail?: string;
  reason?: string;
};

async function requestJson<T>(path: string, options?: Options): Promise<T> {
  let payload: T | null;
  try {
    payload = await fetchJSON<T>(`${API_ROOT}${path}`, options);
  } catch (error) {
    if (!(error instanceof HttpError)) throw error;
    let details: ErrorPayload | null = null;
    try {
      details = JSON.parse(error.message) as ErrorPayload;
    } catch {
      details = null;
    }
    throw new SecCapReasonApiError(
      details?.message ?? details?.error ?? details?.detail ?? error.message,
      error.status,
      details?.code ?? details?.reason ?? null
    );
  }
  if (payload == null) {
    throw new SecCapReasonApiError('The server returned an empty response.', 502);
  }
  return payload;
}

function unwrapTask(payload: ReasonTaskResponse | ReasonTask): ReasonTask {
  return 'task' in payload ? payload.task : payload;
}

function unwrapAdminTask(payload: { task: ReasonAdminTask } | ReasonAdminTask): ReasonAdminTask {
  return 'task' in payload ? payload.task : payload;
}

export function createSecCapReasonApiClient(): SecCapReasonApiClient {
  return {
    async claimNext() {
      const payload = await requestJson<ReasonTaskResponse | ReasonTask | { task: null }>(
        '/tasks/claim-next',
        { method: 'post' }
      );
      if ('task' in payload && payload.task == null) return null;
      return unwrapTask(payload as ReasonTaskResponse | ReasonTask);
    },
    async listMyTasks(options = {}) {
      const search = new URLSearchParams();
      if (options.status) search.set('status', options.status);
      if (options.query?.trim()) search.set('query', options.query.trim());
      if (options.sort) search.set('sort', options.sort);
      if (options.order) search.set('order', options.order);
      if (options.cursor) search.set('cursor', options.cursor);
      if (options.limit) search.set('limit', String(options.limit));
      const suffix = search.size > 0 ? `?${search.toString()}` : '';
      const payload = await requestJson<ReasonTaskListResponse | ReasonTaskSummary[]>(
        `/me/tasks${suffix}`
      );
      if (!Array.isArray(payload)) return payload;
      const completed = payload.filter((task) => task.first_committed_at_ms !== null);
      return {
        tasks: payload,
        summary: {
          assigned_episodes: payload.length,
          completed_episodes: completed.length,
          labeled_segments: completed.reduce((total, task) => total + task.fail_segment_count, 0),
          output_duration_s: completed.reduce(
            (total, task) => total + (task.output_duration_s ?? 0),
            0
          ),
        },
        next_cursor: null,
      };
    },
    async getTask(episodeId) {
      const payload = await requestJson<ReasonTaskResponse | ReasonTask>(
        `/tasks/${encodeURIComponent(episodeId)}`
      );
      return unwrapTask(payload);
    },
    async patchSegment(episodeId, segmentIndex, input: PatchReasonSegmentInput) {
      const payload = await requestJson<ReasonTaskResponse | ReasonTask>(
        `/tasks/${encodeURIComponent(episodeId)}/segments/${segmentIndex}`,
        { method: 'patch', json: input }
      );
      return unwrapTask(payload);
    },
    async commitTask(episodeId, input: CommitReasonTaskInput) {
      const payload = await requestJson<ReasonTaskResponse | ReasonTask>(
        `/tasks/${encodeURIComponent(episodeId)}/commit`,
        { method: 'post', json: input }
      );
      return unwrapTask(payload);
    },
    async commitAndClaimNext(episodeId, input) {
      return requestJson<CommitAndClaimNextReasonTaskResult>(
        `/tasks/${encodeURIComponent(episodeId)}/commit-and-claim-next`,
        { method: 'post', json: input }
      );
    },
    async listAdminTasks(offset = 0, limit = 200, query = '') {
      const search = new URLSearchParams({ offset: String(offset), limit: String(limit) });
      if (query.trim()) search.set('query', query.trim());
      const payload = await requestJson<ReasonAdminTaskListResponse | ReasonAdminTask[]>(
        `/admin/tasks?${search.toString()}`
      );
      return Array.isArray(payload)
        ? { tasks: payload, has_more: false, next_offset: null }
        : payload;
    },
    async listSubmissionHistory(episodeId) {
      const payload = await requestJson<{ submissions: ReasonSubmissionHistoryEntry[] }>(
        `/admin/tasks/${encodeURIComponent(episodeId)}/submissions`
      );
      return payload.submissions;
    },
    async listResults(options) {
      const search = new URLSearchParams();
      if (options.episode_id?.trim()) search.set('episode_id', options.episode_id.trim());
      if (options.operator?.trim()) search.set('operator', options.operator.trim());
      if (options.from_ms !== undefined) search.set('from_ms', String(options.from_ms));
      if (options.to_ms !== undefined) search.set('to_ms', String(options.to_ms));
      if (options.cursor) search.set('cursor', options.cursor);
      if (options.limit) search.set('limit', String(options.limit));
      return requestJson<ReasonResultListResponse>(`/admin/results?${search.toString()}`);
    },
    async getResult(episodeId, revision) {
      const search = new URLSearchParams();
      if (revision !== undefined) search.set('revision', String(revision));
      const suffix = search.size > 0 ? `?${search.toString()}` : '';
      const payload = await requestJson<{ result: ReasonResultDetail } | ReasonResultDetail>(
        `/admin/results/${encodeURIComponent(episodeId)}${suffix}`
      );
      return 'result' in payload ? payload.result : payload;
    },
    async getRuntimeInfo() {
      return requestJson<ReasonRuntimeInfo>('/runtime-info');
    },
    async migrateSegmentContractV2(dryRun, limit, options: ReasonSegmentContractV2Options = {}) {
      const payload = await requestJson<{ result: ReasonSegmentContractV2Result }>(
        '/admin/segment-contract-v2',
        {
          method: 'post',
          json: {
            dry_run: dryRun,
            limit,
            ...options,
          },
        }
      );
      return payload.result;
    },
    async reassignTask(episodeId, operator) {
      const payload = await requestJson<{ task: ReasonAdminTask } | ReasonAdminTask>(
        `/admin/tasks/${encodeURIComponent(episodeId)}/reassign`,
        { method: 'post', json: { operator } }
      );
      return unwrapAdminTask(payload);
    },
    async retrySync(episodeId) {
      const payload = await requestJson<{ task: ReasonAdminTask } | ReasonAdminTask>(
        `/admin/tasks/${encodeURIComponent(episodeId)}/retry-sync`,
        { method: 'post' }
      );
      return unwrapAdminTask(payload);
    },
    async refreshSource(episodeId, expectedSourceAuditHash) {
      const payload = await requestJson<ReasonSourceRefreshResponse>(
        `/admin/tasks/${encodeURIComponent(episodeId)}/refresh-source`,
        {
          method: 'post',
          json: { expected_source_audit_hash: expectedSourceAuditHash },
        }
      );
      return payload.task;
    },
    async diagnoseSource(episodeId) {
      return requestJson<ReasonSourceDiagnostic>(
        `/admin/tasks/${encodeURIComponent(episodeId)}/source-diagnostic`
      );
    },
    async runIngestion(dryRun, maxPages, fromBeginning = false) {
      return requestJson<ReasonIngestionResult>('/admin/ingestion/run', {
        method: 'post',
        json: {
          dry_run: dryRun,
          max_pages: maxPages,
          ...(fromBeginning ? { cursor: { audited_at_ms: 0, episode_id: '' } } : {}),
        },
        timeout: 120_000,
      });
    },
    async getStats() {
      const payload = await requestJson<ReasonStatsResponse | ReasonStats>('/stats');
      return 'stats' in payload ? payload.stats : payload;
    },
    async recordClientEvent(input: ReasonClientEventInput) {
      await requestJson<{ accepted: true }>('/client-events', {
        method: 'post',
        json: input,
        timeout: 5_000,
      });
    },
    async listReasonOptions() {
      const payload = await requestJson<
        | {
            options: ReasonOption[];
            categories?: Array<{
              code: ReasonOption['category_code'];
              label: string;
              sort_order: number;
              options: ReasonOption[];
            }>;
          }
        | ReasonOption[]
      >('/reason-options');
      if (Array.isArray(payload)) return payload;
      if (!payload.categories) return payload.options;
      return payload.categories.flatMap((category) =>
        category.options.map((option) => ({
          ...option,
          category_code: category.code,
          category_label: category.label,
          category_sort_order: category.sort_order,
        }))
      );
    },
    async getLeaderboard() {
      const payload = await requestJson<ReasonLeaderboardResponse | ReasonLeaderboardData>(
        '/leaderboard'
      );
      return 'leaderboard' in payload ? payload.leaderboard : payload;
    },
  };
}

export const secCapReasonApi = createSecCapReasonApiClient();
